// src/components/porter/PorterDispatch.jsx
import React, { useState, useEffect } from 'react';
import { 
  getPorterQuote, 
  createPorterOrder, 
  generateRequestId 
} from '../../services/porterService';
import Button from '../common/Button';
import Modal from '../common/Modal';
import LocationInput from '../common/LocationInput';

const PorterDispatch = ({ order, onSuccess, onError, showAlert }) => {
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState('initial'); // initial, quote, confirm, processing, success
  const [quoteData, setQuoteData] = useState(null);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [locations, setLocations] = useState({
    pickup: {
      lat: null,
      lng: null,
      address: '',
      contactName: order?.customerName || '',
      contactPhone: order?.customerPhone || '',
    },
    drop: {
      lat: null,
      lng: null,
      address: '',
      contactName: order?.customerName || '',
      contactPhone: order?.customerPhone || '',
    }
  });
  const [deliveryInstructions, setDeliveryInstructions] = useState('');

  const openDispatchModal = () => {
    setShowModal(true);
    setStep('initial');
    // Reset form state
    setQuoteData(null);
    setSelectedVehicle(null);
  };

  const closeDispatchModal = () => {
    setShowModal(false);
  };

  const handleLocationChange = (type, field, value) => {
    setLocations(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value
      }
    }));
  };

  const handleDeliveryInstructionsChange = (e) => {
    setDeliveryInstructions(e.target.value);
  };

  const validateLocations = () => {
    if (!locations.pickup.lat || !locations.pickup.lng || !locations.pickup.address) {
      showAlert('Please enter a valid pickup address', 'error');
      return false;
    }
    
    if (!locations.drop.lat || !locations.drop.lng || !locations.drop.address) {
      showAlert('Please enter a valid drop address', 'error');
      return false;
    }
    
    if (!locations.pickup.contactName || !locations.pickup.contactPhone) {
      showAlert('Please enter pickup contact details', 'error');
      return false;
    }
    
    if (!locations.drop.contactName || !locations.drop.contactPhone) {
      showAlert('Please enter drop contact details', 'error');
      return false;
    }
    
    return true;
  };

  const fetchPorterQuote = async () => {
    if (!validateLocations()) return;
    
    try {
      setIsLoading(true);
      
      const customerDetails = {
        name: locations.pickup.contactName,
        phoneNumber: locations.pickup.contactPhone
      };
      
      const quoteResult = await getPorterQuote(
        { lat: locations.pickup.lat, lng: locations.pickup.lng },
        { lat: locations.drop.lat, lng: locations.drop.lng },
        customerDetails
      );
      
      setQuoteData(quoteResult);
      setStep('quote');
      setIsLoading(false);
    } catch (error) {
      console.error('Error fetching Porter quote:', error);
      showAlert(`Failed to get quote: ${error.message}`, 'error');
      setIsLoading(false);
    }
  };

  const handleVehicleSelect = (vehicle) => {
    setSelectedVehicle(vehicle);
    setStep('confirm');
  };

  const formatCurrency = (minorAmount, currency = 'INR') => {
    // Convert from minor units (paise) to major units (rupees)
    const amount = minorAmount / 100;
    
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const createOrder = async () => {
    if (!selectedVehicle) {
      showAlert('Please select a vehicle type', 'error');
      return;
    }
    
    try {
      setIsLoading(true);
      setStep('processing');
      
      const requestId = generateRequestId();
      const instructions = deliveryInstructions 
        ? [deliveryInstructions] 
        : [];
      
      // Use order ID as additional comment to track in Porter dashboard
      const additionalComment = `Order #${order.id}`;
      
      const orderResult = await createPorterOrder(
        requestId,
        locations.pickup,
        locations.drop,
        additionalComment,
        instructions
      );
      
      setStep('success');
      setIsLoading(false);
      
      // Update the parent component with the result
      if (onSuccess) {
        onSuccess(orderResult);
      }
      
      // Show success message
      showAlert(`Porter delivery scheduled. Tracking ID: ${orderResult.order_id}`, 'success');
      
      // Close modal after a short delay
      setTimeout(() => {
        closeDispatchModal();
      }, 3000);
    } catch (error) {
      console.error('Error creating Porter order:', error);
      showAlert(`Failed to create order: ${error.message}`, 'error');
      setIsLoading(false);
      setStep('confirm'); // Return to confirmation step to allow retrying
    }
  };

  // Render different content based on current step
  const renderStepContent = () => {
    switch (step) {
      case 'initial':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Dispatch via Porter</h3>
            <p className="text-sm text-gray-500">
              Enter pickup and drop locations to get quotes for delivery.
            </p>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">Pickup Location</label>
                <LocationInput
                  placeholder="Enter pickup address"
                  value={locations.pickup.address}
                  onChange={(address, lat, lng) => {
                    handleLocationChange('pickup', 'address', address);
                    handleLocationChange('pickup', 'lat', lat);
                    handleLocationChange('pickup', 'lng', lng);
                  }}
                />
                
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      value={locations.pickup.contactName}
                      onChange={(e) => handleLocationChange('pickup', 'contactName', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      value={locations.pickup.contactPhone}
                      onChange={(e) => handleLocationChange('pickup', 'contactPhone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Drop Location</label>
                <LocationInput
                  placeholder="Enter drop address"
                  value={locations.drop.address}
                  onChange={(address, lat, lng) => {
                    handleLocationChange('drop', 'address', address);
                    handleLocationChange('drop', 'lat', lat);
                    handleLocationChange('drop', 'lng', lng);
                  }}
                />
                
                <div className="mt-2 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Name</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      value={locations.drop.contactName}
                      onChange={(e) => handleLocationChange('drop', 'contactName', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Contact Phone</label>
                    <input
                      type="text"
                      className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                      value={locations.drop.contactPhone}
                      onChange={(e) => handleLocationChange('drop', 'contactPhone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Delivery Instructions (Optional)</label>
                <textarea
                  rows={3}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
                  placeholder="Add any special instructions for the delivery partner"
                  value={deliveryInstructions}
                  onChange={handleDeliveryInstructionsChange}
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={closeDispatchModal}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={fetchPorterQuote}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    Getting Quotes...
                  </div>
                ) : 'Get Quotes'}
              </Button>
            </div>
          </div>
        );
        
      case 'quote':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Available Vehicles</h3>
            <p className="text-sm text-gray-500">
              Select a vehicle type for your delivery.
            </p>
            
            <div className="space-y-4">
              {quoteData && quoteData.vehicles && quoteData.vehicles.length > 0 ? (
                quoteData.vehicles.map((vehicle, index) => (
                  <div 
                    key={`${vehicle.type}-${index}`}
                    className="border border-gray-200 rounded-md p-4 hover:border-orange-300 hover:bg-orange-50 cursor-pointer"
                    onClick={() => handleVehicleSelect(vehicle)}
                  >
                    <div className="flex justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">{vehicle.type}</h4>
                        {vehicle.capacity && (
                          <p className="text-sm text-gray-500">
                            Capacity: {vehicle.capacity.value} {vehicle.capacity.unit}
                          </p>
                        )}
                        {vehicle.size && (
                          <p className="text-sm text-gray-500">
                            Size: {vehicle.size.length.value}{vehicle.size.length.unit} x {vehicle.size.breadth.value}{vehicle.size.breadth.unit} x {vehicle.size.height.value}{vehicle.size.height.unit}
                          </p>
                        )}
                        {vehicle.eta && (
                          <p className="text-sm text-gray-500">
                            ETA: {vehicle.eta.value} {vehicle.eta.unit}s
                          </p>
                        )}
                      </div>
                      <div>
                        <p className="text-lg font-bold text-orange-600">
                          {formatCurrency(vehicle.fare.minor_amount, vehicle.fare.currency)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-500">No vehicles available for this route.</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep('initial')}
              >
                Back
              </Button>
            </div>
          </div>
        );
        
      case 'confirm':
        return (
          <div className="space-y-6">
            <h3 className="text-lg font-medium text-gray-900">Confirm Order</h3>
            
            <div className="bg-gray-50 p-4 rounded-md">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <p className="text-sm font-medium text-gray-500">Vehicle Type</p>
                  <p className="text-sm text-gray-900">{selectedVehicle.type}</p>
                </div>
                
                <div className="flex justify-between">
                  <p className="text-sm font-medium text-gray-500">Fare</p>
                  <p className="text-sm font-medium text-orange-600">
                    {formatCurrency(selectedVehicle.fare.minor_amount, selectedVehicle.fare.currency)}
                  </p>
                </div>
                
                {selectedVehicle.eta && (
                  <div className="flex justify-between">
                    <p className="text-sm font-medium text-gray-500">Estimated Pickup Time</p>
                    <p className="text-sm text-gray-900">{selectedVehicle.eta.value} {selectedVehicle.eta.unit}s</p>
                  </div>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Pickup</h4>
                <p className="text-sm text-gray-900">{locations.pickup.address}</p>
                <p className="text-sm text-gray-500">
                  {locations.pickup.contactName}, {locations.pickup.contactPhone}
                </p>
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Drop</h4>
                <p className="text-sm text-gray-900">{locations.drop.address}</p>
                <p className="text-sm text-gray-500">
                  {locations.drop.contactName}, {locations.drop.contactPhone}
                </p>
              </div>
              
              {deliveryInstructions && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Delivery Instructions</h4>
                  <p className="text-sm text-gray-900">{deliveryInstructions}</p>
                </div>
              )}
            </div>
            
            <div className="flex justify-end space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setStep('quote')}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={createOrder}
                disabled={isLoading}
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"></path>
                    </svg>
                    Processing...
                  </div>
                ) : 'Confirm Order'}
              </Button>
            </div>
          </div>
        );
        
      case 'processing':
        return (
          <div className="text-center py-8">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-600"></div>
            </div>
            <p className="mt-4 text-lg text-gray-700">Creating your Porter delivery order...</p>
            <p className="mt-2 text-sm text-gray-500">This may take a few moments.</p>
          </div>
        );
        
      case 'success':
        return (
          <div className="text-center py-8">
            <div className="flex justify-center">
              <div className="rounded-full h-16 w-16 bg-green-100 flex items-center justify-center">
                <svg className="h-10 w-10 text-green-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
            </div>
            <h3 className="mt-4 text-lg font-medium text-gray-900">Order Created Successfully!</h3>
            <p className="mt-2 text-sm text-gray-500">Your Porter delivery order has been created. You can track the status of your delivery.</p>
          </div>
        );
        
      default:
        return null;
    }
  };

  return (
    <>
      <Button
        type="button"
        variant="primary"
        onClick={openDispatchModal}
        className="flex items-center"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path d="M8 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM15 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z" />
          <path d="M3 4a1 1 0 00-1 1v10a1 1 0 001 1h1.05a2.5 2.5 0 014.9 0H10a1 1 0 001-1v-5h2.038A2.968 2.968 0 0115 12.995V13a2.5 2.5 0 014.9 0H20a1 1 0 001-1v-5a4 4 0 00-4-4h-3.5V3a1 1 0 00-1-1H3z" />
        </svg>
        Dispatch via Porter
      </Button>
      
      {showModal && (
        <Modal 
          isOpen={showModal} 
          onClose={step === 'processing' ? null : closeDispatchModal}
          title=""
        >
          <div className="p-4">
            {renderStepContent()}
          </div>
        </Modal>
      )}
    </>
  );
};

export default PorterDispatch;